// src/components/Pentests/tabs/AIInsights.js
//
// Phase 1 (2026-05-14) restructure: Executive Summary + Top 10 Critical
// Findings + Remediation Themes. The per-finding remediation card list moved
// to each Finding's expanded row in the Findings tab. Top 10 is derived
// frontend-side (severity DESC, cvss DESC, reference_number ASC). Remediation
// Themes lazy-fires on tab mount when the server-cached row is stale or
// missing; the backend caches up to 24h or until any finding changes.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AlertTriangle, ArrowUpRight, Layers, Loader2, RefreshCw, Sparkles, Target,
} from 'lucide-react';
import { pentestsAPI } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import { EmptyState, PrimaryCTA } from '../../common/Shinobi';
import { parseServerDate } from '../../../utils/time';

const SEVERITY_BADGE = {
  info: 'bg-slate-100 text-slate-700 border-slate-300',
  low: 'bg-blue-100 text-blue-700 border-blue-300',
  medium: 'bg-yellow-100 text-yellow-700 border-yellow-300',
  high: 'bg-orange-100 text-orange-700 border-orange-300',
  critical: 'bg-red-100 text-red-700 border-red-300',
};

// Worst -> best so sort descending puts criticals first.
const SEVERITY_RANK = { critical: 5, high: 4, medium: 3, low: 2, info: 1 };

const PRIORITY_BADGE = {
  P0: 'bg-red-100 text-red-700 border-red-300',
  P1: 'bg-orange-100 text-orange-700 border-orange-300',
  P2: 'bg-yellow-100 text-yellow-700 border-yellow-300',
  P3: 'bg-blue-100 text-blue-700 border-blue-300',
  P4: 'bg-slate-100 text-slate-700 border-slate-300',
};

function relTime(iso) {
  if (!iso) return null;
  const d = parseServerDate(iso);
  if (!d || isNaN(d.getTime())) return iso;
  const diff = Date.now() - d.getTime();
  if (diff < 30 * 1000) return 'just now';
  if (diff < 60 * 1000) return `${Math.round(diff / 1000)}s ago`;
  if (diff < 60 * 60 * 1000) return `${Math.round(diff / 60000)}m ago`;
  if (diff < 24 * 60 * 60 * 1000) return `${Math.round(diff / 3600000)}h ago`;
  return d.toLocaleString();
}

function GeneratingPanel({ label = 'Wasabi Shinobi AI is thinking', sublabel, dense = false }) {
  return (
    <div className={`text-center ${dense ? 'py-6' : 'py-10'}`}>
      <div className="relative inline-flex items-center justify-center mb-3">
        <span className="absolute inline-flex w-14 h-14 rounded-full bg-wasabi-100 animate-ping" />
        <span className="relative inline-flex w-12 h-12 rounded-full bg-wasabi-100 border border-wasabi-300 items-center justify-center">
          <Sparkles className="w-5 h-5 text-shinobi-green" />
        </span>
      </div>
      <div className="flex items-center justify-center gap-2 text-sm font-semibold text-slate-800">
        <Loader2 className="w-3.5 h-3.5 animate-spin text-shinobi-green" />
        <span>{label}</span>
      </div>
      <div className="text-xs text-slate-500 mt-1.5">
        {sublabel || 'Drafting fresh analysis from the latest findings. This usually takes 5-15 seconds.'}
      </div>
      <div className="mt-4 h-1 max-w-[16rem] mx-auto rounded-full bg-cyber-100 overflow-hidden">
        <div className="h-full rounded-full bg-shinobi-green/60 animate-pulse" style={{ width: '60%' }} />
      </div>
    </div>
  );
}

export default function AIInsights() {
  const { id: pentestIdParam } = useParams();
  const pentestId = Number(pentestIdParam);
  const { isPentester } = useAuth();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [findings, setFindings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [generatingSummary, setGeneratingSummary] = useState(false);
  const [generatingThemes, setGeneratingThemes] = useState(false);
  // Track whether we've already triggered the lazy themes generation for this
  // mount, so a re-render does not refire it.
  const [themesAutoTriggered, setThemesAutoTriggered] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [insights, findingList] = await Promise.all([
        pentestsAPI.aiInsights.get(pentestId),
        pentestsAPI.findings.list(pentestId),
      ]);
      setData(insights);
      setFindings(findingList || []);
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  }, [pentestId]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const onGenerateSummary = async () => {
    setGeneratingSummary(true);
    setError(null);
    try {
      await pentestsAPI.aiInsights.generateSummary(pentestId);
      await fetchAll();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setGeneratingSummary(false);
    }
  };

  const onGenerateThemes = useCallback(async ({ force = false } = {}) => {
    setGeneratingThemes(true);
    setError(null);
    try {
      const fresh = await pentestsAPI.aiInsights.generateRemediationThemes(pentestId, { force });
      setData((prev) => (prev ? { ...prev, remediation_themes: fresh } : prev));
    } catch (err) {
      setError(err?.response?.data?.detail || err.message);
    } finally {
      setGeneratingThemes(false);
    }
  }, [pentestId]);

  // Lazy auto-trigger remediation themes on first tab mount when the cache is
  // missing AND the AI service is configured AND the operator can generate
  // (Pentester role).
  useEffect(() => {
    if (themesAutoTriggered) return;
    if (!data || data.available === false) return;
    if (data.remediation_themes) return;
    if (!isPentester) return;
    if (findings.length === 0) return;
    setThemesAutoTriggered(true);
    onGenerateThemes();
  }, [data, isPentester, findings.length, themesAutoTriggered, onGenerateThemes]);

  // Top 10 critical findings (frontend-derived). Excludes false positives and
  // findings already verified Fixed / Won't Fix - the same definition the
  // backend's open-findings filter uses.
  const top10 = useMemo(() => {
    const open = findings.filter((f) => {
      if (f.false_positive) return false;
      if (f.verification_status === 'fixed' || f.verification_status === 'wont_fix') return false;
      return true;
    });
    return [...open].sort((a, b) => {
      const ra = SEVERITY_RANK[a.severity] || 0;
      const rb = SEVERITY_RANK[b.severity] || 0;
      if (ra !== rb) return rb - ra;
      const ca = a.cvss_score == null ? -1 : Number(a.cvss_score);
      const cb = b.cvss_score == null ? -1 : Number(b.cvss_score);
      if (ca !== cb) return cb - ca;
      return (a.reference_number || 0) - (b.reference_number || 0);
    }).slice(0, 10);
  }, [findings]);

  const findingsByRef = useMemo(() => {
    const m = new Map();
    findings.forEach((f) => m.set(f.reference_number, f));
    return m;
  }, [findings]);

  const navigateToFinding = (findingId) => {
    navigate(`/pentests/${pentestId}/findings?focus=${findingId}`);
  };

  if (loading) return <div className="text-slate-500">Loading AI insights...</div>;

  return (
    <div className="space-y-6">
      {data?.available === false && (
        <div className="rounded-xl border border-amber-300 bg-amber-100 p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-semibold text-amber-700">AI service not configured</p>
            <p className="text-amber-700/80 mt-1">
              Set <code className="font-mono text-xs px-1 rounded bg-amber-100">ANTHROPIC_API_KEY</code> in the backend environment and restart the API to enable AI insights. Existing analyses (if any) will still display below.
            </p>
          </div>
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-red-300 bg-red-100 p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <SummaryPanel
        summary={generatingSummary ? null : data?.summary}
        available={!!data?.available}
        canGenerate={isPentester && !!data?.available}
        generating={generatingSummary}
        hadPriorSummary={!!data?.summary}
        onGenerate={onGenerateSummary}
      />

      <Top10Panel
        findings={top10}
        totalOpen={findings.filter((f) => !f.false_positive && f.verification_status !== 'fixed' && f.verification_status !== 'wont_fix').length}
        onNavigateToFinding={navigateToFinding}
      />

      <RemediationThemesPanel
        themes={data?.remediation_themes}
        available={!!data?.available}
        canGenerate={isPentester && !!data?.available}
        generating={generatingThemes}
        onRegenerate={() => onGenerateThemes({ force: true })}
        findingsByRef={findingsByRef}
        onNavigateToFinding={navigateToFinding}
      />
    </div>
  );
}

function SummaryPanel({ summary, available, canGenerate, generating, hadPriorSummary, onGenerate }) {
  const result = summary?.result || {};
  const hasSummary = !!summary;

  return (
    <div className="rounded-2xl border border-cyber-300/40 bg-cyber-50/60 overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4 border-b border-cyber-300/40 bg-gradient-to-r from-shinobi-green/5 to-transparent">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl border border-wasabi-300 bg-wasabi-100 flex items-center justify-center text-shinobi-green">
            <Sparkles className="w-5 h-5" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Engagement summary</div>
            <h3 className="text-base font-semibold text-slate-900">Executive Summary</h3>
            {summary && (
              <div className="text-xs text-slate-500 mt-0.5">
                Generated {relTime(summary.created_at)}
                {summary.duration_seconds ? ` (${summary.duration_seconds.toFixed(1)}s)` : ''}
                {summary.model_used ? ` via ${summary.model_used}` : ''}
              </div>
            )}
          </div>
        </div>
        {canGenerate && (
          <PrimaryCTA
            variant="green"
            icon={hasSummary ? RefreshCw : Sparkles}
            iconPosition="left"
            loading={generating}
            onClick={onGenerate}
          >
            {hasSummary ? 'Regenerate' : 'Generate'}
          </PrimaryCTA>
        )}
      </div>

      <div className="px-5 py-5">
        {generating ? (
          <GeneratingPanel
            label={hadPriorSummary ? 'Wasabi Shinobi AI is regenerating the summary' : 'Wasabi Shinobi AI is drafting the summary'}
            sublabel="Synthesizing scope, pre-engagement context, and current findings into a fresh executive view."
          />
        ) : !hasSummary ? (
          <div className="text-center py-8">
            <Sparkles className="w-8 h-8 text-slate-600 mx-auto mb-3" />
            <p className="text-sm text-slate-500 mb-1">No summary generated yet.</p>
            {canGenerate ? (
              <p className="text-xs text-slate-500">
                Click <span className="text-shinobi-green">Generate</span> to produce an executive summary from the engagement scope, pre-engagement context, and current findings.
              </p>
            ) : !available ? (
              <p className="text-xs text-slate-500">Configure the AI service to enable generation.</p>
            ) : (
              <p className="text-xs text-slate-500">Only Pentesters can generate AI summaries.</p>
            )}
          </div>
        ) : (
          <div className="space-y-5 text-sm text-slate-800 leading-relaxed">
            {result.executive_summary && (
              <p className="text-slate-900">{result.executive_summary}</p>
            )}

            {Array.isArray(result.key_risks) && result.key_risks.length > 0 && (
              <div>
                <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-semibold mb-2 flex items-center gap-2">
                  <Target className="w-3.5 h-3.5" /> Key Risks
                </div>
                <ul className="space-y-1.5">
                  {result.key_risks.map((r, i) => (
                    <li key={i} className="flex gap-2">
                      <span className="text-shinobi-green shrink-0">{i + 1}.</span>
                      <span className="text-slate-700">{r}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {result.business_impact && (
              <div>
                <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-semibold mb-2">
                  Business Impact
                </div>
                <p className="text-slate-700">{result.business_impact}</p>
              </div>
            )}

            {Array.isArray(result.recommended_priorities) && result.recommended_priorities.length > 0 && (
              <div>
                <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-semibold mb-2">
                  Recommended Priorities
                </div>
                <ol className="space-y-1.5 list-none">
                  {result.recommended_priorities.map((p, i) => (
                    <li key={i} className="flex gap-2">
                      <span className="text-shinobi-green font-mono text-xs mt-0.5 shrink-0">P{i + 1}</span>
                      <span className="text-slate-700">{p}</span>
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Top10Panel({ findings, totalOpen, onNavigateToFinding }) {
  if (totalOpen === 0) {
    return (
      <div className="rounded-2xl border border-cyber-300/40 bg-cyber-50/60 overflow-hidden">
        <div className="flex items-center gap-3 px-5 py-4 border-b border-cyber-300/40">
          <div className="w-10 h-10 rounded-xl border border-wasabi-300 bg-wasabi-100 flex items-center justify-center text-shinobi-green">
            <Target className="w-5 h-5" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Open critical surface</div>
            <h3 className="text-base font-semibold text-slate-900">Top 10 Critical Findings</h3>
          </div>
        </div>
        <div className="p-6 text-center">
          <Target className="w-8 h-8 text-slate-600 mx-auto mb-2" />
          <p className="text-sm text-slate-500">No open findings.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-cyber-300/40 bg-cyber-50/60 overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4 border-b border-cyber-300/40">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl border border-wasabi-300 bg-wasabi-100 flex items-center justify-center text-shinobi-green">
            <Target className="w-5 h-5" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Open critical surface</div>
            <h3 className="text-base font-semibold text-slate-900">Top 10 Critical Findings</h3>
            <div className="text-xs text-slate-500 mt-0.5">
              Highest severity / CVSS first. {findings.length} of {totalOpen} open shown.
            </div>
          </div>
        </div>
      </div>

      <ul className="divide-y divide-cyber-300/20">
        {findings.map((f) => {
          const sev = f.severity || 'info';
          const sevCls = SEVERITY_BADGE[sev] || SEVERITY_BADGE.info;
          return (
            <li key={f.id}>
              <button
                type="button"
                onClick={() => onNavigateToFinding(f.id)}
                className="w-full text-left px-5 py-3 flex items-center gap-3 hover:bg-cyber-200/40 transition group"
              >
                <span className="font-mono text-xs text-slate-500 shrink-0 w-12">#{f.reference_number}</span>
                <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded border ${sevCls} shrink-0`}>
                  {sev}
                </span>
                <span className="flex-1 text-sm text-slate-800 truncate">{f.title}</span>
                {f.cvss_score != null && (
                  <span className="font-mono text-xs text-slate-500 shrink-0">CVSS {Number(f.cvss_score).toFixed(1)}</span>
                )}
                <ArrowUpRight className="w-4 h-4 text-slate-500 group-hover:text-shinobi-green transition shrink-0" />
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function RemediationThemesPanel({
  themes, available, canGenerate, generating, onRegenerate, findingsByRef, onNavigateToFinding,
}) {
  const themeList = Array.isArray(themes?.result?.themes) ? themes.result.themes : [];

  return (
    <div className="rounded-2xl border border-cyber-300/40 bg-cyber-50/60 overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4 border-b border-cyber-300/40 bg-gradient-to-r from-purple-500/5 to-transparent">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl border border-purple-300 bg-purple-100 flex items-center justify-center text-purple-700">
            <Layers className="w-5 h-5" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Clustered workstreams</div>
            <h3 className="text-base font-semibold text-slate-900">Remediation Themes</h3>
            {themes && (
              <div className="text-xs text-slate-500 mt-0.5">
                Generated {relTime(themes.created_at)}
                {themes.duration_seconds ? ` (${themes.duration_seconds.toFixed(1)}s)` : ''}
                {themes.model_used ? ` via ${themes.model_used}` : ''}
                {' · cached for up to 24h or until any finding changes'}
              </div>
            )}
          </div>
        </div>
        {canGenerate && themes && (
          <PrimaryCTA
            variant="green"
            icon={RefreshCw}
            iconPosition="left"
            loading={generating}
            onClick={onRegenerate}
          >
            Regenerate
          </PrimaryCTA>
        )}
      </div>

      <div className="px-5 py-5">
        {generating ? (
          <GeneratingPanel
            label="Wasabi Shinobi AI is clustering open findings"
            sublabel="Grouping findings into 5-8 workstreams that share a fix path so the team can work through one theme at a time."
          />
        ) : !themes ? (
          <div className="text-center py-8">
            <Layers className="w-8 h-8 text-slate-600 mx-auto mb-3" />
            <p className="text-sm text-slate-500 mb-1">No themes yet.</p>
            {!available ? (
              <p className="text-xs text-slate-500">Configure the AI service to enable generation.</p>
            ) : !canGenerate ? (
              <p className="text-xs text-slate-500">Only Pentesters can generate remediation themes.</p>
            ) : (
              <p className="text-xs text-slate-500">Themes will appear automatically once findings are present.</p>
            )}
          </div>
        ) : themeList.length === 0 ? (
          <EmptyState
            icon={Layers}
            accent="green"
            title="No open findings to cluster"
            description="Once there are open findings, Wasabi Shinobi AI will group them into actionable workstreams here."
          />
        ) : (
          <div className="space-y-3">
            {themeList.map((theme, idx) => (
              <ThemeCard
                key={idx}
                theme={theme}
                findingsByRef={findingsByRef}
                onNavigateToFinding={onNavigateToFinding}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ThemeCard({ theme, findingsByRef, onNavigateToFinding }) {
  const refs = Array.isArray(theme.finding_refs) ? theme.finding_refs : [];
  const priority = theme.priority || 'P4';
  const priorityCls = PRIORITY_BADGE[priority.toUpperCase()] || PRIORITY_BADGE.P4;

  return (
    <div className="rounded-xl border border-cyber-300/40 bg-cyber-100/40 p-4">
      <div className="flex items-start gap-3 mb-2 flex-wrap">
        <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded border ${priorityCls} shrink-0`}>
          {priority}
        </span>
        <span className="flex-1 text-sm font-semibold text-slate-900">{theme.title}</span>
        {theme.estimated_effort && (
          <span className="text-xs text-slate-500">
            <span className="uppercase tracking-wider text-slate-500 mr-1">Effort</span>
            <span className="font-mono text-slate-700">{theme.estimated_effort}</span>
          </span>
        )}
      </div>
      {theme.summary && (
        <p className="text-sm text-slate-700 leading-relaxed mb-3">{theme.summary}</p>
      )}
      {refs.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          <span className="text-[10px] uppercase tracking-wider text-slate-500 mt-1 mr-1">Covers</span>
          {refs.map((ref) => {
            const finding = findingsByRef.get(ref);
            const cls = finding
              ? 'bg-wasabi-100 text-wasabi-700 border-wasabi-300 hover:bg-wasabi-100'
              : 'bg-slate-100 text-slate-500 border-slate-300';
            return (
              <button
                key={ref}
                type="button"
                disabled={!finding}
                onClick={() => finding && onNavigateToFinding(finding.id)}
                title={finding ? finding.title : `Finding #${ref} not found in current set`}
                className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-mono transition ${cls}`}
              >
                #{ref}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
